import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Badge, Card, PageHeader, LinkButton, SectionHeader,
  CheckBox, RadioButton,
  GuestInfoSection, OptionsSection, ShoppingSection, RouteInfoSection,
  StickyBottomBar, PriceDisplay, ReservationProgressBar,
  ModalWrapper, ModalCloseButton,
} from '../../../components/common';
import VehicleInfoSection from '../../../components/service/VehicleInfoSection';
import PaymentAgreementCard from '../../../components/service/PaymentAgreementCard';
import { RESERVATION_STEPS, TOTAL_RESERVATION_STEPS } from '../../../constants/reservationSteps';
import { makeServiceLabels, makeDirectionLabels } from '../../../constants/serviceLabels';
import SERVICE_ICON_MAP from '../../../constants/serviceIconMap';
import { paymentMethods } from '../../../constants/uiConstants';
import EasyPayProviderModal from '../../../components/service/EasyPayProviderModal';
import { executePayment, isPortOneReady } from '../../../api/portone';
import { pricingService, reservationService, shoppingService } from '../../../api/services';
import { useAvailabilityCheck } from '../../../hooks/useAvailabilityCheck';
import useAlertModal from '../../../hooks/useAlertModal';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useMoney } from '../../../hooks/useMoney';
import { translateApiError, appendErrorReason } from '../../../utils/apiError';

function PromoCodeInput({ appliedCoupon, onApply, onRemove }) {
  const { t } = useTranslation();
  const money = useMoney();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleApply = async () => {
    if (!code.trim()) return;
    setError('');
    const debug = process.env.NODE_ENV !== 'production';
    if (debug) console.log('[Coupon] 검증 요청:', { code: code.trim() });
    try {
      const result = await pricingService.validateCoupon(code);
      if (debug) console.log('[Coupon] 검증 응답:', result);
      if (result.valid) {
        if (debug) console.log('[Coupon] ✅ 적용 성공:', { name: result.name, discount_type: result.discount_type, discount_value: result.discount_value });
        onApply({
          code: code.trim().toUpperCase(),
          name: result.name,
          discount_value: Number(result.discount_value),
          discount_type: result.discount_type,
        });
      } else {
        console.warn('[Coupon] ❌ 검증 실패:', result.message);
        setError(result.message || t('payment.alert_invalid_code'));
      }
    } catch (e) {
      console.error('[Coupon] 🚨 API 호출 에러:', e);
      setError(t('payment.alert_check_code_failed'));
    }
  };

  const handleRemove = () => {
    setCode('');
    setError('');
    onRemove();
  };

  return (
    <div className="space-y-2">
      {!appliedCoupon ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder={t('payment.discount_code_placeholder')}
            className="flex-1 h-10 px-3 border border-mono-200 rounded-lg text-sm text-mono-900 placeholder:text-mono-400 focus:outline-none focus:border-orange-500 transition uppercase"
          />
          <Button level="level1" onClick={handleApply} className="shrink-0 h-10">{t('payment.apply')}</Button>
        </div>
      ) : (
        <div className="bg-orange-50 rounded-lg p-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Icon name="coupon" size={18} className="text-orange-500 shrink-0" />
            <span className="text-sm font-bold text-mono-900 truncate min-w-0">{appliedCoupon.name}</span>
            <span className="text-sm font-bold text-orange-500 shrink-0">
              {appliedCoupon.discount_type === 'percentage'
                ? `-${appliedCoupon.discount_value}%`
                : `-${money(appliedCoupon.discount_value)}`}
            </span>
          </div>
          <button onClick={handleRemove} className="text-mono-400 hover:text-mono-600 shrink-0">
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export default function TransferPayment() {
  const { t } = useTranslation();
  const money = useMoney();
  const serviceLabels = makeServiceLabels(t);
  const directionLabels = makeDirectionLabels(t);
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 언어 전환은 full-reload를 수반하므로(LanguageContext) history state가 소실된다.
  // 결제 페이지에서 언어만 바꿨는데 랜딩으로 튕기지 않도록, BookingSettings와 동일하게
  // sessionStorage에 결제 진입 페이로드(bookingData/reservationId)를 캐시하고 state 누락 시 복구.
  const paymentCacheKey = `vvt_payment_${type || 'airport'}`;
  const cachedPayload = useMemo(() => {
    if (location.state?.bookingData || location.state?.reservationId) return null;
    try {
      const raw = sessionStorage.getItem(paymentCacheKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }, [location.state, paymentCacheKey]);

  const locationData = location?.state?.bookingData || cachedPayload?.bookingData || null;
  const incomingReservationId = location.state?.reservationId || cachedPayload?.reservationId || null;

  useEffect(() => {
    if (location.state?.bookingData || location.state?.reservationId) {
      try {
        sessionStorage.setItem(paymentCacheKey, JSON.stringify({
          bookingData: location.state.bookingData || null,
          reservationId: location.state.reservationId || null,
        }));
      } catch {}
    }
  }, [location.state, paymentCacheKey]);

  // #29: bookingData/reservationId가 state·캐시 양쪽 모두 없을 때만 랜딩으로 리다이렉트.
  useEffect(() => {
    if (!locationData && !incomingReservationId) {
      navigate(`/transfer/${type || 'airport'}`, { replace: true });
    }
  }, [locationData, incomingReservationId, navigate, type]);

  const { check: checkAvailability, modal: availabilityModal, showConflict } = useAvailabilityCheck({
    serviceType: type || 'airport',
    dateTime: locationData?.dateTime || '',
    passengers: 1,
    onSelectOtherDate: () => navigate(-1),
  });

  const [data, setData] = useState(locationData || {
    dateTime: '', vehicle: '', reserverName: '', phone: '', email: '',
    options: [], shoppingProducts: [],
    departure: '', departureTime: '', departureNotes: '', arrival: '',
    stopovers: [],
    subtotal: 0, originalPrice: 0,
    flightNumber: '', direction: '',
  });
  const [vehicleData, setVehicleData] = useState({ carName: '', seat: '', capacity: '', luggage: '', usageTimeBadge: null, waitInfo: {} });

  /* 쿠폰(프로모 코드) 적용 상태 */
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, name, discount_value, discount_type }
  const [showCouponLimitModal, setShowCouponLimitModal] = useState(false);

  /* 약관 관련 상태 */
  const [terms, setTerms] = useState([]);
  const [termsAgreed, setTermsAgreed] = useState({});
  const [agreeAll, setAgreeAll] = useState(false);

  useEffect(() => {
    pricingService.getVehicleInfo(type || 'airport').then((v) => setVehicleData(v)).catch((e) => console.warn(e.message));

    // 어드민 가격 변경을 결제 단계에서도 실시간 반영 — 서버 calculate_price(/api/price/search,
    // 예약생성과 동일)로 운송 가격을 라이브 재계산. 쇼핑 포함 예약은 합산이 별도라 스냅샷 유지,
    // 검색 실패/미해결 시 catch 로 기존 스냅샷 폴백(회귀 방지).
    const payload = locationData?.reservationPayload;
    const hasShopping = (payload?.shopping_items || []).length > 0;
    // 가격 일치 핵심: region(ski/golf 리조트명, hospital 지역키 등) 없이 주소만 보내면 calculate_price 가
    //   지역가격을 못 찾고 default base 로 폴백(예: ski 499,000 → 399,000) → 설정페이지와 금액 불일치.
    //   → BookingSettings 가 그 가격을 낸 동일 searchPrice body(region 포함)를 그대로 재사용한다.
    //   (없을 때만 payload 로 재구성 — 폴백에도 region 을 동봉해 매칭 확률을 높인다.)
    const searchBody = locationData?.priceSearchBody || (payload ? {
      service_type: type || 'airport',
      departure_region: payload.departure_address || undefined,
      destination_region: payload.destination_address || undefined,
      departure: payload.departure_address || undefined,
      arrival: payload.destination_address || undefined,
      date: (payload.pickup_date || '').split('T')[0] || undefined,
      pickup_time: payload.pickup_time || undefined,
      passenger_count: payload.passenger_count || 1,
      selected_options: (payload.options || []).map((o) => ({ id: o.option_id, quantity: o.quantity })),
      stopover_count: (payload.stopovers || []).length,
    } : null);
    if (searchBody && !hasShopping) {
      pricingService.search(searchBody).then((pd) => {
        if (pd?.total != null) {
          // transfer 는 displayPrice 가 originalPrice 기준 → 전체 총액(pd.total)을 넣는다.
          setData((prev) => ({
            ...prev,
            subtotal: Number(pd.total),
            originalPrice: Number(pd.total),
          }));
        }
      }).catch(() => {});
    }

    /* 약관 목록 로드 — 백엔드 데이터 없으면 빈 배열 */
    pricingService.getPaymentTerms().then((list) => {
      const safeList = Array.isArray(list) ? list.filter((t) => t.title) : [];
      setTerms(safeList);
      const initial = {};
      safeList.forEach((t) => { initial[t.id] = false; });
      setTermsAgreed(initial);
    }).catch(() => {
      setTerms([]);
      setTermsAgreed({});
    });
  }, [type]);

  const isAirport = type === 'airport';

  // 예약 대기에서 직접 진입한 경우(location.state.reservationId / 캐시 복구분)와
  // BookingSettings에서 진입 후 결제 시점에 새로 생성한 예약(createdReservationId)을 통합 관리.
  const [createdReservationId, setCreatedReservationId] = useState(incomingReservationId);
  const reservationId = createdReservationId;

  // 서버 예약 데이터 → bookingData 형태로 변환 (예약 대기에서 결제 진입 시 사용)
  // 주의: incomingReservationId(외부에서 들어온 ID)에만 트리거. 결제 시점에 새로 만든 createdReservationId는
  // BE 응답이 BookingSettings의 가격과 동일하므로 다시 fetch할 필요 없음.
  useEffect(() => {
    if (!incomingReservationId) return;
    reservationService.getById(incomingReservationId)
      .then((res) => {
        const serverPrice = res.total_price;
        // bookingData가 없으면(pending에서 직접 진입) 서버 데이터로 모든 필드 채우기
        if (!locationData) {
          const route = res.route || {};
          const dep = route.departure || {};
          const dest = route.destination || {};
          const dateTime = route.reservation_date && dep.time
            ? `${route.reservation_date}T${dep.time.slice(0, 5)}`
            : '';
          setData({
            dateTime,
            vehicle: res.vehicle_name || '',
            reserverName: res.booker_name || '',
            phone: res.booker_phone || '',
            email: res.booker_email || '',
            options: (res.selected_options || []).map((o) => ({
              name: o.name,
              value: o.quantity > 1 ? t('common.items_count', { n: o.quantity }) : t('complete.select'),
              price: Number(o.total_price) || 0,
            })),
            shoppingProducts: (res.shopping?.items || []).map((it) => ({
              brand: it.category,
              name: it.name,
              price: Number(it.price) || 0,
              qty: it.quantity || 1,
              image_url: it.image_url,
            })),
            departure: dep.address || '',
            departureTime: dep.time?.slice(0, 5) || '',
            departureNotes: res.memo || '',
            arrival: dest.address || '',
            destination: dest.address || '',
            stopovers: route.stopovers || [],
            venueName: route.stopovers?.[0]?.address || res.venue_name || '',
            subtotal: serverPrice || 0,
            originalPrice: serverPrice || 0,
            flightNumber: res.flight_number || '',
            flightArrivalTime: res.flight_time || '',
            direction: route.direction?.replace(/_/g, '-') || '',
          });
        } else if (serverPrice != null) {
          setData((prev) => ({ ...prev, originalPrice: serverPrice }));
        }
      })
      .catch(() => {});
  }, [incomingReservationId, locationData]);

  // 기본 선택은 card. simple(간편결제)은 2026-06-09 재가맹으로 옵션 복원됐으나 기본값은 card 유지.
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [easyPayOpen, setEasyPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const { showAlert, alertModal } = useAlertModal();

  /* 필수 약관 모두 동의했는지 — PaymentAgreementCard에서 콜백으로 관리 */
  const allRequiredAgreed = agreeAll;

  // BE 한국어 데이터 (옵션/쇼핑/주소/차량) 현재 UI 언어로 번역.
  // BookingSettings 와 동일 패턴 — 결제 페이지에서는 미적용이라 한국어 잔존하던 회귀 보완.
  const optionTextsBundle = useMemo(() => {
    const out = [];
    (data.options || []).forEach((o) => out.push(o.name || ''));
    return out;
  }, [data.options]);
  const tOptionTexts = useDeepLTranslated(optionTextsBundle);
  const shoppingTextsBundle = useMemo(() => {
    const out = [];
    (data.shoppingProducts || []).forEach((p) => out.push(p.brand || '', p.name || ''));
    return out;
  }, [data.shoppingProducts]);
  const tShoppingTexts = useDeepLTranslated(shoppingTextsBundle);
  const stopoverTextsBundle = useMemo(() => {
    const out = [];
    (data.stopovers || []).forEach((s) => out.push(s.address || '', s.name || ''));
    return out;
  }, [data.stopovers]);
  const tStopoverTexts = useDeepLTranslated(stopoverTextsBundle);
  const routeTextsBundle = useMemo(() => [
    data.departure || '',
    data.departureName || '',
    data.departureDetail || '',
    data.departureNotes || '',
    data.arrival || '',
    data.destinationName || '',
    data.arrivalDetail || '',
  ], [data.departure, data.departureName, data.departureDetail, data.departureNotes, data.arrival, data.destinationName, data.arrivalDetail]);
  const tRouteTexts = useDeepLTranslated(routeTextsBundle);
  const vehicleTextsBundle = useMemo(() => [
    vehicleData.carName || '',
    vehicleData.seat || '',
    vehicleData.capacity || '',
    vehicleData.luggage || '',
  ], [vehicleData.carName, vehicleData.seat, vehicleData.capacity, vehicleData.luggage]);
  const tVehicleTexts = useDeepLTranslated(vehicleTextsBundle);

  const translatedOptions = useMemo(() => (data.options || []).map((o, i) => ({
    ...o,
    name: tOptionTexts[i] || o.name,
  })), [data.options, tOptionTexts]);
  const translatedShopping = useMemo(() => (data.shoppingProducts || []).map((p, i) => ({
    ...p,
    brand: tShoppingTexts[i * 2] || p.brand,
    name: tShoppingTexts[i * 2 + 1] || p.name,
  })), [data.shoppingProducts, tShoppingTexts]);
  const translatedStopovers = useMemo(() => (data.stopovers || []).map((s, i) => ({
    ...s,
    address: tStopoverTexts[i * 2] || s.address,
    name: tStopoverTexts[i * 2 + 1] || s.name,
  })), [data.stopovers, tStopoverTexts]);
  const translatedRoute = {
    departure: tRouteTexts[0] || data.departure,
    departureName: tRouteTexts[1] || data.departureName,
    departureDetail: tRouteTexts[2] || data.departureDetail,
    departureNotes: tRouteTexts[3] || data.departureNotes,
    arrival: tRouteTexts[4] || data.arrival,
    destinationName: tRouteTexts[5] || data.destinationName,
    arrivalDetail: tRouteTexts[6] || data.arrivalDetail,
  };
  const translatedVehicle = {
    ...vehicleData,
    carName: tVehicleTexts[0] || vehicleData.carName,
    seat: tVehicleTexts[1] || vehicleData.seat,
    capacity: tVehicleTexts[2] || vehicleData.capacity,
    luggage: tVehicleTexts[3] || vehicleData.luggage,
  };

  // 쿠폰 할인액 계산 (FE 표시용 — 실제 결제 금액은 BE create 응답의 total_price 사용)
  const couponDiscountAmount = appliedCoupon
    ? (appliedCoupon.discount_type === 'percentage'
        ? Math.round((data.originalPrice || 0) * appliedCoupon.discount_value / 100)
        : Math.min(appliedCoupon.discount_value, data.originalPrice || 0))
    : 0;
  const displayPrice = Math.max(0, (data.originalPrice || 0) - couponDiscountAmount);

  /* 쿠폰 적용 — 코드가 변경되면 이전에 생성한 reservation은 무효화하여 다음 결제 시점에 새로 생성하도록 함 */
  const handleApplyCoupon = (coupon) => {
    // 쿠폰은 1건만 사용 가능 — 이미 적용된 쿠폰이 있으면 모달 노출
    if (appliedCoupon) {
      setShowCouponLimitModal(true);
      return;
    }
    setAppliedCoupon(coupon);
    if (!incomingReservationId) {
      setCreatedReservationId(null);
    }
  };
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    if (!incomingReservationId) {
      setCreatedReservationId(null);
    }
  };

  const handlePayment = async (methodOverride) => {
    // 약관 동의 검증
    if (!allRequiredAgreed) {
      showAlert(t('payment.alert_terms_required'));
      return;
    }
    const available = await checkAvailability();
    if (!available) return;

    if (!isPortOneReady()) {
      console.error('[Payment] PortOne 미준비 — IMP_CODE/CHANNEL_KEY/SDK 확인 필요');
      showAlert(t('payment.alert_payment_module_not_ready'));
      return;
    }
    // 간편결제(simple): 결제수단 선택 모달을 띄우고, 모달에서 고른 provider 로 재호출.
    const method = (typeof methodOverride === 'string' ? methodOverride : null) || paymentMethod;
    if (method === 'simple') { setEasyPayOpen(true); return; }

    setPaying(true);
    // 모바일에서 포트원 모달이 전체 화면으로 표시되도록 스크롤 최상단 이동
    window.scrollTo(0, 0);
    try {
      // 1. 결제 대상 reservation 확보 — 없으면 BookingSettings에서 넘어온 payload + 쿠폰 코드로 신규 생성
      let payReservationId = reservationId;
      if (!payReservationId) {
        const payload = locationData?.reservationPayload;
        if (!payload) {
          console.error('[Payment] reservationPayload 없음 — 결제 차단');
          showAlert(t('payment.alert_no_reservation'));
          setPaying(false);
          return;
        }
        try {
          const created = await reservationService.create({
            ...payload,
            ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
          });
          if (process.env.NODE_ENV !== 'production') {
            console.log('[Payment] reservation 생성 완료:', { id: created.id, total_price: created.total_price, discount_amount: created.discount_amount });
          }
          payReservationId = created.id;
          setCreatedReservationId(created.id);
          // BugHerd #194: 별도 ShoppingOrder createOrder 호출 제거.
          // 이제 BookingSettings 가 reservationPayload.shopping_items 로 전송하고
          // BE reservations.py 가 동일 트랜잭션에서 ShoppingOrder + total_price 합산까지 처리.
          // → PG 결제 금액에 쇼핑 합산 정확히 반영 (이전엔 운송비만 청구되던 회귀 해결).
        } catch (err) {
          console.error('[Payment] reservation 생성 실패:', err);
          // BugHerd #4: 결제 직전 409 slot_conflict 는 alert 대신 가용성 모달과 동일 UX로.
          if (err?.code === 'slot_conflict') {
            showConflict?.();
          } else {
            showAlert(translateApiError(err, t, 'booking.alert_reservation_failed'));
          }
          setPaying(false);
          return;
        }
      }

      const result = await executePayment({
        reservationId: payReservationId,
        paymentType: 'initial',
        paymentMethod: method,
        buyerName: data.reserverName,
        buyerTel: data.phone,
        buyerEmail: data.email,
        serviceType: type,
        redirectUrl: `${window.location.origin}/transfer/${type}/complete?id=${payReservationId}`,
      });
      // 결제 완료 — 언어 전환용 결제 페이지 캐시는 더 이상 필요 없음
      try { sessionStorage.removeItem(paymentCacheKey); } catch {}
      // 무통장입금: 가상계좌 발급 완료 → 계좌 정보와 함께 완료 페이지로 이동
      if (result?.status === 'ready') {
        navigate(`/transfer/${type}/complete`, { state: { reservationId: payReservationId, vbank: result } });
      } else {
        navigate(`/transfer/${type}/complete`, { state: { reservationId: payReservationId } });
      }
    } catch (err) {
      // 예약은 생성됐고 결제(executePayment) 단계 실패/취소 → 취소(info)와 실패(error)를 분리.
      // PG/서버가 내려준 사유(err.message)를 base 뒤에 "(사유: XXX)" 형태로 부착.
      const cancelled = err?.code === 'payment_cancelled' || err?.code === 'payment_not_completed';
      const base = translateApiError(err, t, cancelled ? 'payment.alert_payment_cancelled' : 'payment.alert_payment_failed');
      showAlert(
        appendErrorReason(base, err, t),
        { variant: cancelled ? 'info' : 'error' },
      );
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-36 sm:pb-32">
        <div className="max-w-form mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header: Back + Title ===== */}
          <PageHeader title={t('payment.title')} rightAction={<LinkButton onClick={() => navigate(`/transfer/${type}/booking`)}>{t('payment.edit')}</LinkButton>} />

          {/* ===== Service info ===== */}
          <Card>
            <div className="space-y-2">
              <Badge variant="service" serviceType="transfer">{t('nav.private_transfer')}</Badge>
              <h2 className="text-base font-bold text-mono-900">{serviceLabels[type] || serviceLabels.airport}</h2>
              <p className="text-2sm text-mono-500">{t('payment.scheduled_use_time')}: {data.dateTime ? data.dateTime.replace('T', ' ') : '-'}</p>
            </div>
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('payment.vehicle_info')} />
            <VehicleInfoSection
              images={translatedVehicle.images}
              carName={translatedVehicle.carName}
              usageTimeBadge={translatedVehicle.usageTimeBadge}
              specs={[
                { icon: 'van', text: translatedVehicle.seat },
                { icon: 'person', text: translatedVehicle.capacity },
                { icon: 'bag', text: translatedVehicle.luggage },
              ]}
              waitInfo={translatedVehicle.waitInfo}
              /* #110: 야간/주말 할증 표기 제거 — 시스템 계산엔 그대로 포함됨 */
              showReview={false}
              showPriceNote={false}
            />
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <Card>
            <GuestInfoSection name={data.reserverName} phone={data.phone} email={data.email} />
          </Card>

          {/* ===== 반려동물 정보 (pet 서비스 전용) ===== */}
          {type === 'pet' && (
            <Card>
              <SectionHeader variant="card" title={t('booking.pet_info_title')} />
              <div className="flex items-start gap-4">
                <span className="text-2sm font-medium text-mono-500 w-20 shrink-0">{t('booking.pet_count_label')}</span>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <span className="text-2sm font-medium text-mono-900">{(data.petCount || 1)}{t('booking.pet_unit')}</span>
                  {data.petNotes && (
                    <span className="text-2sm font-medium text-mono-500 whitespace-pre-wrap break-words">{data.petNotes}</span>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* ===== 선택 옵션 ===== */}
          <Card>
            <OptionsSection options={translatedOptions.map(opt => ({
              label: opt.name,
              value: opt.value,
              price: `(+${money(opt.price)})`,
            }))} />
            {/* #110: 야간/주말 할증 표기는 제거 — 시스템 가격 계산엔 그대로 포함됨 */}
          </Card>

          {/* ===== 항공편 정보 (공항→도착지 only) ===== */}
          {isAirport && data.direction === 'from-airport' && data.flightNumber && (
            <Card>
              <SectionHeader variant="card" title={t('payment.flight_info')} />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2sm font-medium text-mono-500">{t('booking.flight_number')}</span>
                  <span className="text-2sm font-medium text-mono-900">{data.flightNumber}</span>
                </div>
                {data.flightArrivalTime && (
                  <div className="flex items-center justify-between">
                    <span className="text-2sm font-medium text-mono-500">{t('booking.flight_arrival_time')}</span>
                    <span className="text-2sm font-medium text-mono-900">{data.flightArrivalTime.replace('T', ' ')}</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* ===== 프라이빗 쇼핑 서비스 ===== */}
          {isAirport && translatedShopping.length > 0 && (
            <Card>
              <ShoppingSection
                items={translatedShopping.map(p => ({
                  brand: p.brand,
                  name: p.name,
                  price: `${money(p.price)}`,
                  qty: p.qty,
                  image_url: p.image_url,
                }))}
                total={money(data.originalPrice)}
              />
            </Card>
          )}

          {/* ===== 이동 정보 ===== */}
          <Card>
            <RouteInfoSection
              directionLabel={directionLabels[data.direction] || directionLabels[type] || directionLabels.airport}
              from={{
                label: t('form.departure'),
                name: translatedRoute.departureName,
                address: translatedRoute.departure,
                detail: translatedRoute.departureDetail,
                time: data.departureTime,
                notes: translatedRoute.departureNotes,
                icon: (data.direction?.startsWith('from-') && type !== 'airport') ? (SERVICE_ICON_MAP[type] || 'van') : 'van',
              }}
              to={{
                label: data.direction?.startsWith('from-') ? t('form.destination') : (type === 'airport' ? t('complete.destination_airport') : type === 'hospital' ? t('complete.destination_hospital') : t('form.destination')),
                name: translatedRoute.destinationName,
                address: translatedRoute.arrival || data.destination,
                detail: translatedRoute.arrivalDetail,
              }}
              via={(type === 'golf' || type === 'ski') && data.venueName ? { label: type === 'golf' ? t('form.golf_course') : t('form.ski_resort'), address: data.venueName, icon: SERVICE_ICON_MAP[type] || 'place' } : undefined}
              stopovers={translatedStopovers}
              arrivalIcon={(type === 'golf' || type === 'ski') ? 'place' : (data.direction?.startsWith('from-') ? 'place' : (SERVICE_ICON_MAP[type] || 'place'))}
            />
          </Card>

          {/* ===== 프로모 코드 — 신규 예약 흐름에서만 노출 (예약 대기에서 진입한 경우 제외) ===== */}
          {!incomingReservationId && (
            <Card>
              <SectionHeader variant="card" title={t('payment.discount_code')} />
              <PromoCodeInput
                appliedCoupon={appliedCoupon}
                onApply={handleApplyCoupon}
                onRemove={handleRemoveCoupon}
              />
            </Card>
          )}

          {/* ===== 결제 수단 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('payment.method')} />
            <div className="space-y-3">
              {paymentMethods.map((m) => (
                <RadioButton
                  key={m.id}
                  name="payment-method"
                  label={m.labelKey ? t(m.labelKey) : m.label}
                  value={m.id}
                  checked={paymentMethod === m.id}
                  onChange={() => setPaymentMethod(m.id)}
                />
              ))}
            </div>
          </Card>

          {/* ===== 약관 동의 ===== */}
          <PaymentAgreementCard onAllAgreedChange={(v) => setAgreeAll(v)} />

        </div>
      </main>

      {/* ===== Sticky bottom bar ===== */}
      <StickyBottomBar step={RESERVATION_STEPS.payment} totalSteps={TOTAL_RESERVATION_STEPS}>
        <PriceDisplay
          approx
          label={t('payment.estimated_payment')}
          amount={displayPrice}
          badge={appliedCoupon ? t('payment.discount_badge') : undefined}
          originalAmount={appliedCoupon ? data.originalPrice : undefined}
        />
        <Button onClick={handlePayment} disabled={!allRequiredAgreed || paying} width="responsive-130">{paying ? t('payment.paying') : t('payment.pay_button')}</Button>
      </StickyBottomBar>
      {availabilityModal}
      {alertModal}
      <EasyPayProviderModal
        open={easyPayOpen}
        onClose={() => setEasyPayOpen(false)}
        onSelect={(providerId) => { setEasyPayOpen(false); handlePayment(providerId); }}
      />

      {/* ===== 쿠폰 1건 제한 안내 모달 ===== */}
      {showCouponLimitModal && (
        <ModalWrapper onClose={() => setShowCouponLimitModal(false)} variant="compact" gap="gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-mono-900">{t('payment.coupon_limit_title')}</h2>
            <ModalCloseButton onClick={() => setShowCouponLimitModal(false)} />
          </div>
          <p className="text-sm text-mono-700">
            {t('payment.coupon_limit_message')}
          </p>
          <Button level="blue" fullWidth onClick={() => setShowCouponLimitModal(false)}>
            {t('common.confirm')}
          </Button>
        </ModalWrapper>
      )}
    </>
  );
}
